import { useCallback, useDeferredValue, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { ActivityPanel, hasActivity } from '../home/ActivityPanel'
import { FormCatalog } from '../home/FormCatalog'
import { HeroBackdrop } from '../home/HeroArt'
import { HomeHero } from '../home/HomeHero'
import { featuredForms, greeting, resultsAnnouncement, searchForms } from '../home/model'
import { QuickStartPalette, useQuickStartShortcut } from '../home/QuickStartPalette'
import { useHomeData, useStartForm } from '../home/useHomeData'

/**
 * Front page. For end users the job is simply to start a request: a
 * personal greeting, one search box (Enter starts the best match, ⌘K opens
 * quick start) and the form catalog. The user's own drafts, approvals and
 * recent requests sit quietly to the side and vanish when there are none.
 */
export default function HomePage() {
    const { user, accountName } = useAuth()
    const data = useHomeData()
    const { start, starting } = useStartForm()
    const [query, setQuery] = useState('')
    const [paletteOpen, setPaletteOpen] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const catalogRef = useRef<HTMLDivElement>(null)

    const deferredQuery = useDeferredValue(query)
    const searching = deferredQuery.trim().length > 0
    const results = useMemo(
        () => (searching && data.forms ? searchForms(data.forms, deferredQuery) : null),
        [searching, data.forms, deferredQuery],
    )
    const featured = useMemo(() => (data.forms ? featuredForms(data.forms, 3) : null), [data.forms])
    const title = greeting(user?.name || accountName, new Date().getHours())
    const waitingCount = data.inbox?.length ?? 0
    const activity = hasActivity(data)

    const togglePalette = useCallback(() => setPaletteOpen((o) => !o), [])
    useQuickStartShortcut(togglePalette)

    const submitSearch = () => {
        const top = (query.trim() && data.forms ? searchForms(data.forms, query) : [])[0]
        if (top) void start(top)
    }
    const focusFirstCard = () => {
        catalogRef.current?.querySelector<HTMLButtonElement>('[data-form-card]:not(:disabled)')?.focus()
    }

    return (
        <div className="flex flex-col gap-10 lg:gap-12">
            {/* Hero: greeting + search, with the user's activity on the right on large screens. */}
            <section
                aria-label="Start a request"
                className="relative isolate overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-background lg:grid lg:min-h-[22rem] lg:grid-cols-[minmax(0,1fr)_minmax(20rem,24rem)]"
            >
                <HeroBackdrop className="-z-10 opacity-80" />
                <HomeHero
                    title={title}
                    query={query}
                    onQueryChange={setQuery}
                    onSubmit={submitSearch}
                    onArrowDown={focusFirstCard}
                    onOpenPalette={() => setPaletteOpen(true)}
                    inputRef={inputRef}
                    featured={featured}
                    onStart={start}
                    starting={starting}
                    waitingCount={waitingCount}
                />
                <aside
                    aria-label="Your activity"
                    className="hidden lg:m-4 lg:ml-0 lg:flex lg:flex-col lg:overflow-hidden lg:rounded-xl lg:border lg:bg-card/85 lg:shadow-sm lg:backdrop-blur-sm"
                >
                    <ActivityPanel drafts={data.drafts} recent={data.recent} inbox={data.inbox} showIdle className="flex-1" />
                </aside>
            </section>

            <div aria-live="polite" role="status" className="sr-only">
                {results ? resultsAnnouncement(results.length, deferredQuery) : ''}
            </div>

            <div ref={catalogRef} id="catalog">
                <FormCatalog
                    forms={data.forms}
                    error={data.formsError}
                    onRetry={data.reloadForms}
                    results={results}
                    query={deferredQuery}
                    onClearQuery={() => { setQuery(''); inputRef.current?.focus() }}
                    onStart={start}
                    starting={starting}
                />
            </div>

            {/* Small screens: activity follows the catalog, and only when there is some. */}
            {activity && (
                <aside aria-label="Your activity" className="rounded-xl border bg-card lg:hidden">
                    <ActivityPanel drafts={data.drafts} recent={data.recent} inbox={data.inbox} />
                </aside>
            )}

            <QuickStartPalette
                open={paletteOpen}
                onOpenChange={setPaletteOpen}
                forms={data.forms}
                drafts={data.drafts}
                waitingCount={waitingCount}
                onStart={start}
            />
        </div>
    )
}
